import React, {
    useEffect,
    useState,
    useImperativeHandle,
    forwardRef,
    useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { get, post } from "../../common/apiClient";
import { toast } from "react-toastify";
import logo from "../common/images/pi.png";

/**
 * SidebarCreateMailbox
 * Lets the user create a temporary mailbox from an authenticated session.
 * Uses POST /api/public/mailboxes.
 */
const SidebarCreateMailbox = forwardRef(({ onMailboxCreated, currentMailbox }, ref) => {
    const { t } = useTranslation();
    const [domains, setDomains] = useState([]);
    const [username, setUsername] = useState("");
    const [domainId, setDomainId] = useState("");
    const [loading, setLoading] = useState(false);

    const resetForm = useCallback(() => {
        setUsername("");
        if (domains.length) {
            setDomainId(domains[0]._id);
        }
    }, [domains]);

    useImperativeHandle(ref, () => ({ resetForm }), [resetForm]);

    useEffect(() => {
        fetchDomains();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Mirror the current mailbox address back into the form inputs
    useEffect(() => {
        if (!currentMailbox?.address || !domains.length) return;
        const [localPart = "", domainPart = ""] = String(currentMailbox.address).split("@");
        if (localPart) setUsername(localPart);
        if (domainPart) {
            const matched = domains.find(
                (d) => d.name && d.name.toLowerCase() === domainPart.toLowerCase()
            );
            if (matched?._id) setDomainId(matched._id);
        }
    }, [currentMailbox, domains]);

    const fetchDomains = async () => {
        try {
            const res = await get("/public/domains");
            const list = Array.isArray(res) ? res : res.data || [];
            setDomains(list);
            if (!domainId && list.length) {
                setDomainId(list[0]._id);
            }
        } catch (err) {
            console.error("Failed to load domains", err);
            toast.error("Failed to load domains");
        }
    };

    const handleCreate = async (useRandom) => {
        if (!domainId && !domains.length) {
            toast.error("No domain available");
            return;
        }
        if (!useRandom && !username.trim()) {
            toast.error(t("enterUsername") || "Please enter a username or use Random");
            return;
        }

        setLoading(true);
        try {
            const body = { domainId };
            if (useRandom) {
                body.random = true;
            } else {
                body.username = username.trim();
            }

            const res = await post("/public/mailboxes", body);
            const data = res.data || res;

            toast.success("Mailbox created!");

            // Update the username input to reflect the created address
            if (data.address) {
                const parts = String(data.address).split("@");
                if (parts.length === 2) {
                    const [local, domainName] = parts;
                    setUsername(local);
                    const matched = domains.find(
                        (d) => d.name && d.name.toLowerCase() === domainName.toLowerCase()
                    );
                    if (matched?._id) setDomainId(matched._id);
                }
            }

            onMailboxCreated &&
                onMailboxCreated({
                    id: data.id,
                    address: data.address,
                    // expiresAt: data.expiresAt,
                });
        } catch (err) {
            console.error("Create mailbox failed", err);
            toast.error(err.response?.data?.message || "Failed to create mailbox");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col gap-4">
            {/* Brand */}
            <div className="flex items-center gap-2 mb-2">
                <div className="w-12 h-12 rounded-md flex items-center justify-center text-white font-bold">
                    <img
                        src={logo}
                        alt="PiMail"
                        className="w-full h-full object-cover rounded-full"
                    />
                </div>
                <span className="text-xl font-bold text-white">PiMail</span>
            </div>

            {/* Username input */}
            <div className="space-y-2">
                <label className="text-xs text-white/80">{t("enterUsername")}</label>
                <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleCreate(false)}
                    className="w-full rounded-md px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none"
                    placeholder={t("enterUsername")}
                    maxLength={64}
                />
            </div>

            {/* Domain selector */}
            <div className="space-y-2">
                <label className="text-xs text-white/80">{t("selectDomain")}</label>
                <select
                    value={domainId}
                    onChange={(e) => setDomainId(e.target.value)}
                    className="w-full rounded-md px-3 py-2 text-sm bg-white text-gray-900 focus:outline-none"
                >
                    {domains.map((d) => (
                        <option key={d._id} value={d._id}>
                            {d.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Action buttons */}
            <div className="flex gap-2 mt-2">
                <button
                    disabled={loading}
                    onClick={() => handleCreate(false)}
                    className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white text-sm font-medium py-2 rounded-md transition-colors"
                >
                    {loading ? "..." : t("create")}
                </button>
                <button
                    disabled={loading}
                    onClick={() => handleCreate(true)}
                    className="flex-1 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-50 text-gray-900 text-sm font-medium py-2 rounded-md transition-colors"
                >
                    {loading ? "..." : t("random")}
                </button>
            </div>

            <button
                type="button"
                onClick={() => {
                    setUsername("");
                    if (domains.length) setDomainId(domains[0]._id);
                }}
                className="mt-1 text-xs text-white/70 hover:text-white underline self-start"
            >
                {t("cancel")}
            </button>
        </div>
    );
});

SidebarCreateMailbox.displayName = "SidebarCreateMailbox";

export default SidebarCreateMailbox;
